import {
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';

export interface IProjectStep {
  index: number;
  stepTitle: string;
  stepClickHandler: () => void;
  stepButtonText?: string;
  disableStep: boolean;
  stepIconColor: string;
  disabledIconColor: string;
}
interface IProps {
  steps: IProjectStep[];
  currentStep: number;
}

/**
 * Mirrors the hollow-ring / solid-dot pattern from the shared composite
 * Stepper (libs/shared-ui/src/components/composites/Stepper.tsx) used by the
 * original Formative Assessment flow, but driven by this page's own
 * steps/currentStep data instead of a hardcoded 2-title prop shape. Native
 * MUI Stepper + alternativeLabel centers each label under its own column, so
 * long titles never overlap the neighboring step (unlike the
 * QuestionPaperCreate Stepper, which centers labels via absolute
 * positioning with no width limit).
 */
export const ProjectStepper = (props: IProps) => {
  const { steps, currentStep } = props;

  // Nothing selected yet -> every step renders hollow/muted, matching the
  // composite Stepper's `step === undefined` state.
  const started = !steps[0]?.disableStep;
  const activeIndex = started ? currentStep : -1;

  return (
    <Stepper
      alternativeLabel
      connector={
        <StepConnector
          sx={{
            top: 8,
            left: `calc(-40%)`,
            right: `calc(40%)`,
            width: '80%',
            height: '4px',
            backgroundImage: `linear-gradient(to right, ${
              activeIndex >= 0 ? steps[0]?.stepIconColor : steps[0]?.disabledIconColor
            } 0%, ${
              activeIndex >= 0 ? steps[0]?.stepIconColor : steps[0]?.disabledIconColor
            } 50%, ${
              activeIndex >= 1 ? steps[1]?.stepIconColor : steps[1]?.disabledIconColor
            } 50%, ${
              activeIndex >= 1 ? steps[1]?.stepIconColor : steps[1]?.disabledIconColor
            } 100%)`,
            transition: 'backgroundImage 0.5s ease-in-out',
            '& .MuiStepConnector-line': {
              borderColor: 'transparent',
            },
          }}
        />
      }
    >
      {steps.map((step, i) => {
        const isFilled = activeIndex >= i;
        return (
          <Step
            key={step.index}
            sx={{ padding: 0, margin: 0 }}
            onClick={() => {
              if (!step.disableStep) {
                step.stepClickHandler();
              }
            }}
          >
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isFilled
                      ? step.stepIconColor
                      : step.disabledIconColor,
                    cursor: 'pointer',
                  }}
                >
                  {!isFilled && (
                    <Box
                      sx={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '18px',
                        backgroundColor: 'white',
                      }}
                    />
                  )}
                </Box>
              )}
            >
              <Typography
                variant="smallText"
                fontWeight="bold"
                sx={{ color: isFilled ? '#333333' : '#0000005C' }}
              >
                {step.stepTitle}
              </Typography>
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};
